import Phaser from 'phaser';
import { GameScene, pickPreviewSlotForFx } from '../GameScene';
import type { PlayerStateView } from '../../state/GameStateView';
import { COMBAT_CONFIG, GAME_FONT_FAMILY } from '@shared/constants';
import { ClientMessageType } from '@shared/types/messages';
import { getParticleEffect } from '@shared/data/particleEffects';
import { PARTICLE_SHEET } from '../../systems/ParticleSystem';
import { setPerfPhase } from '../../boot/perfProbe';

/**
 * Visuales de estado de entidad driveados por el server: meditación (aura de FX mientras medita),
 * provocación/taunt (overlay rojo "TAUNTEADO") y protección de aparición (aura+viñeta dorada+glow, con
 * FX de ruptura). Cada uno arranca/para su visual según el estado sincronizado. Extraído de GameScene
 * (god-object). El estado (meditationFxHandles, overlays…) vive en GameScene y se accede vía
 * `this.scene.*`. Ver docs/COOLDOWNS_AND_RESOURCES.md, docs/TAUNT.md, docs/SPAWN_PROTECTION.md.
 */
export class EntityStateVfxController {
  constructor(private scene: GameScene) {}

  public handleMeditateState(entityId: string, meditating: boolean): void {
    if (meditating) {
      this.startMeditationVisual(entityId);
    } else {
      this.stopMeditationVisual(entityId);
    }
  }

  public startMeditationVisual(entityId: string): void {
    this.stopMeditationVisual(entityId); // clean previous

    const render = this.scene.playerObjects.get(entityId);
    if (!render) return;

    // FX de partículas (tornado) anclado a los pies del char. El
    // ParticleManager re-resuelve el anchor cada frame, así que sigue al char
    // sin necesidad de update manual. El FX se elige según el nivel del char
    // que medita (local o remoto): una meditación distinta cada 10 niveles.
    const ps = this.scene.room?.state.players.get(entityId) as PlayerStateView | undefined;
    const level = (ps as { level?: number } | undefined)?.level ?? 1;
    const fxId = GameScene.meditationFxIdForLevel(level);
    const def = getParticleEffect(fxId);
    if (def && this.scene.textures.exists(PARTICLE_SHEET.key)) {
      // Mismo anchor que el preview del ParticlePanel (sin offsetY): el FX nace
      // en los pies (slot 'feet'). Antes tenía offsetY: -20, que lo subía 20px y
      // dejaba el pivot demasiado arriba al meditar real (no en el preview).
      const handle = this.scene.particleFx.ensureParticleManager().spawn(fxId, {
        kind: 'entity',
        entityId,
        slot: pickPreviewSlotForFx(def),
      });
      if (handle) this.scene.meditationFxHandles.set(entityId, handle);
    } else if (!def) {
      console.warn(`[Meditate] FX ${fxId} no encontrado en el catálogo`);
    }

    // Blue vignette overlay for local player (borders only, below HUD)
    if (entityId === this.scene.localSessionId) {
      if (!this.scene.meditationOverlay) {
        const { width, height } = this.scene.scale;
        const gfx = this.scene.add.graphics().setScrollFactor(0).setDepth(499); // below HUD (1000)

        // Draw 4 gradient strips on edges
        const edgeW = Math.floor(width * 0.15);
        const edgeH = Math.floor(height * 0.15);
        const color = 0x2244cc;

        // Left edge
        for (let i = 0; i < edgeW; i++) {
          const a = 0.15 * (1 - i / edgeW);
          gfx.fillStyle(color, a);
          gfx.fillRect(i, 0, 1, height);
        }
        // Right edge
        for (let i = 0; i < edgeW; i++) {
          const a = 0.15 * (1 - i / edgeW);
          gfx.fillStyle(color, a);
          gfx.fillRect(width - 1 - i, 0, 1, height);
        }
        // Top edge
        for (let i = 0; i < edgeH; i++) {
          const a = 0.12 * (1 - i / edgeH);
          gfx.fillStyle(color, a);
          gfx.fillRect(0, i, width, 1);
        }
        // Bottom edge (shorter, don't overlap HUD)
        for (let i = 0; i < edgeH / 2; i++) {
          const a = 0.10 * (1 - i / (edgeH / 2));
          gfx.fillStyle(color, a);
          gfx.fillRect(0, height - 1 - i, width, 1);
        }

        this.scene.meditationOverlay = this.scene.add.rectangle(0, 0, 1, 1, 0x000000, 0)
          .setScrollFactor(0).setDepth(499).setVisible(false);
        (this.scene.meditationOverlay as any)._gfx = gfx;
      }
      const gfx = (this.scene.meditationOverlay as any)._gfx as Phaser.GameObjects.Graphics;
      gfx.setVisible(true);
      gfx.setAlpha(0);
      this.scene.meditationOverlay.setVisible(true);

      // Fade in first, then start pulse seamlessly
      this.scene.tweens.add({
        targets: gfx,
        alpha: 0.7,
        duration: 800,
        ease: 'Sine.easeIn',
        onComplete: () => {
          this.scene.tweens.add({
            targets: gfx,
            alpha: { from: 0.7, to: 1.0 },
            duration: 1500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        },
      });
    }
  }

  public stopMeditationVisual(entityId: string): void {
    const handle = this.scene.meditationFxHandles.get(entityId);
    if (handle) {
      this.scene.particleManager?.stop(handle);
      this.scene.meditationFxHandles.delete(entityId);
    }

    if (entityId === this.scene.localSessionId && this.scene.meditationOverlay) {
      const gfx = (this.scene.meditationOverlay as any)._gfx as Phaser.GameObjects.Graphics | undefined;
      if (gfx) {
        this.scene.tweens.killTweensOf(gfx);
        gfx.setVisible(false);
      }
      this.scene.meditationOverlay.setVisible(false);
    }
  }

  // ─── Provocación (taunt) — visual del jugador local ──────────────────────
  // Ver docs/TAUNT.md. Viñeta ROJA de bordes (calco del overlay de meditar) + cartel
  // "TAUNTEADO", sólo para el jugador local mientras esté provocado.

  public syncTauntVisualsFor(entityId: string, taunted: number): void {
    if (entityId !== this.scene.localSessionId) return; // overlay/cartel: sólo el local
    if (taunted === 1) this.showTauntOverlay();
    else this.hideTauntOverlay();
  }

  public showTauntOverlay(): void {
    if (this.scene.tauntOverlayActive) return;
    this.scene.tauntOverlayActive = true;
    const { width, height } = this.scene.scale;

    if (!this.scene.tauntOverlayGfx) {
      const gfx = this.scene.add.graphics().setScrollFactor(0).setDepth(499);
      const edgeW = Math.floor(width * 0.15);
      const edgeH = Math.floor(height * 0.15);
      const color = 0xcc2222;
      for (let i = 0; i < edgeW; i++) {
        const a = 0.18 * (1 - i / edgeW);
        gfx.fillStyle(color, a);
        gfx.fillRect(i, 0, 1, height);
        gfx.fillRect(width - 1 - i, 0, 1, height);
      }
      for (let i = 0; i < edgeH; i++) {
        const a = 0.14 * (1 - i / edgeH);
        gfx.fillStyle(color, a);
        gfx.fillRect(0, i, width, 1);
      }
      for (let i = 0; i < edgeH / 2; i++) {
        const a = 0.1 * (1 - i / (edgeH / 2));
        gfx.fillStyle(color, a);
        gfx.fillRect(0, height - 1 - i, width, 1);
      }
      this.scene.tauntOverlayGfx = gfx;
    }
    const gfx = this.scene.tauntOverlayGfx;
    gfx.setVisible(true).setAlpha(0);
    this.scene.tweens.add({
      targets: gfx,
      alpha: 1,
      duration: 300,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.scene.tweens.add({
          targets: gfx,
          alpha: { from: 1, to: 0.6 },
          duration: 700,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });

    if (!this.scene.tauntLabel) {
      this.scene.tauntLabel = this.scene.add
        .text(width / 2, Math.floor(height * 0.12), 'TAUNTEADO', {
          fontFamily: GAME_FONT_FAMILY,
          fontSize: '30px',
          color: '#ff4444',
          align: 'center',
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(1002);
    }
    this.scene.tauntLabel.setVisible(true).setAlpha(1);
    this.scene.tweens.add({
      targets: this.scene.tauntLabel,
      alpha: { from: 1, to: 0.4 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  public hideTauntOverlay(): void {
    if (!this.scene.tauntOverlayActive) return;
    this.scene.tauntOverlayActive = false;
    if (this.scene.tauntOverlayGfx) {
      this.scene.tweens.killTweensOf(this.scene.tauntOverlayGfx);
      this.scene.tauntOverlayGfx.setVisible(false);
    }
    if (this.scene.tauntLabel) {
      this.scene.tweens.killTweensOf(this.scene.tauntLabel);
      this.scene.tauntLabel.setVisible(false);
    }
  }

  // ─── Protección de aparición (spawn protection) ──────────────────────────
  // Ver docs/SPAWN_PROTECTION.md. Espeja el patrón de meditar: aura por entidad
  // (visible por todos) + viñeta dorada con contador para el local. Al terminar
  // dispara un FX de "ruptura" más marcado.

  public handleSpawnProtectionState(entityId: string, active: boolean, durationMs?: number): void {
    if (active) {
      this.startSpawnProtectionVisual(entityId, durationMs ?? COMBAT_CONFIG.SPAWN_PROTECTION_MS);
    } else {
      this.stopSpawnProtectionVisual(entityId);
    }
  }

  /**
   * Rompe la protección del local desde un input ofensivo que NO genera su propio
   * mensaje (hotkey de hechizo sin target). Manda BREAK_SPAWN_PROTECTION; el FX de
   * ruptura y la viñeta los quita el echo SPAWN_PROTECTION_STATE del server (única
   * fuente, evita doble FX). El flag local evita spamear el mensaje.
   */
  public breakLocalSpawnProtection(): void {
    if (!this.scene.localSpawnProtected) return;
    this.scene.localSpawnProtected = false;
    this.scene.colyseusClient?.send({ type: ClientMessageType.BREAK_SPAWN_PROTECTION } as any);
  }

  public startSpawnProtectionVisual(entityId: string, durationMs: number): void {
    this.stopSpawnProtectionVisual(entityId, true); // limpia previo sin FX de ruptura

    // Halo dorado de contorno sobre los sprites de la entidad (lo ven todos en AOI):
    // un resplandor sutil "iluminado", no partículas. Pulsa suave vía tween sobre la
    // fuerza exterior de los glows. Ver docs/SPAWN_PROTECTION.md.
    const glow = this.applySpawnProtectionGlow(entityId);
    if (glow && glow.controllers.length > 0) {
      const tween = this.scene.tweens.add({
        targets: glow.controllers,
        outerStrength: { from: 1.2, to: 3.2 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.scene.spawnProtectionGlows.set(entityId, { ...glow, tween });
    }

    if (entityId !== this.scene.localSessionId) return;

    // Marca la fase para el perfProbe: la ventana de protección del LOCAL es justo donde el usuario
    // reporta el freeze al terminar. Los long tasks de estos ~5s quedan etiquetados 'protección'.
    setPerfPhase('protección');

    // Local: viñeta dorada en los bordes + contador centrado-arriba.
    this.scene.localSpawnProtected = true;
    const { width, height } = this.scene.scale;
    if (!this.scene.spawnProtectionOverlay) {
      const gfx = this.scene.add.graphics().setScrollFactor(0).setDepth(499);
      const color = 0xd4af37; // dorado/sagrado
      const edgeW = Math.floor(width * 0.15);
      const edgeH = Math.floor(height * 0.15);
      for (let i = 0; i < edgeW; i++) {
        const a = 0.16 * (1 - i / edgeW);
        gfx.fillStyle(color, a);
        gfx.fillRect(i, 0, 1, height);
        gfx.fillRect(width - 1 - i, 0, 1, height);
      }
      for (let i = 0; i < edgeH; i++) {
        const a = 0.13 * (1 - i / edgeH);
        gfx.fillStyle(color, a);
        gfx.fillRect(0, i, width, 1);
      }
      for (let i = 0; i < edgeH / 2; i++) {
        const a = 0.1 * (1 - i / (edgeH / 2));
        gfx.fillStyle(color, a);
        gfx.fillRect(0, height - 1 - i, width, 1);
      }
      this.scene.spawnProtectionOverlay = this.scene.add
        .rectangle(0, 0, 1, 1, 0x000000, 0)
        .setScrollFactor(0)
        .setDepth(499)
        .setVisible(false);
      (this.scene.spawnProtectionOverlay as any)._gfx = gfx;
    }
    const gfx = (this.scene.spawnProtectionOverlay as any)._gfx as Phaser.GameObjects.Graphics;
    gfx.setVisible(true);
    gfx.setAlpha(0);
    this.scene.spawnProtectionOverlay.setVisible(true);
    this.scene.tweens.add({
      targets: gfx,
      alpha: { from: 0.85, to: 1.0 },
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Contador "Protección: N" centrado-arriba, actualizado por timer.
    if (!this.scene.spawnProtectionLabel) {
      this.scene.spawnProtectionLabel = this.scene.add
        .text(width / 2, Math.floor(height * 0.12), '', {
          fontFamily: GAME_FONT_FAMILY,
          fontSize: '20px',
          color: '#ffe9a8',
          stroke: '#5a4a12',
          strokeThickness: 4,
          align: 'center',
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(1001);
    }
    const label = this.scene.spawnProtectionLabel;
    label.setVisible(true).setAlpha(1);
    const endAt = this.scene.time.now + durationMs;
    const updateLabel = (): void => {
      const remainingMs = Math.max(0, endAt - this.scene.time.now);
      const secs = Math.ceil(remainingMs / 1000);
      label.setText(`Protección  ${secs}`);
    };
    updateLabel();
    this.scene.spawnProtectionTimer?.remove();
    this.scene.spawnProtectionTimer = this.scene.time.addEvent({
      delay: 100,
      loop: true,
      callback: updateLabel,
    });
  }

  public stopSpawnProtectionVisual(entityId: string, silent: boolean = false): void {
    // Halo dorado: lo apagamos. Si NO es silent, "destello que se apaga" → el propio
    // halo pega un pico breve y se desvanece + un anillo de luz que se expande.
    const glow = this.scene.spawnProtectionGlows.get(entityId);
    if (glow) {
      this.scene.spawnProtectionGlows.delete(entityId);
      glow.tween?.remove();
      const removeGlows = (): void => this.removeSpawnProtectionGlow(glow);
      if (!silent && glow.controllers.length > 0) {
        // Pico rápido y vuelta a 0; al terminar se remueven los preFX.
        this.scene.tweens.add({
          targets: glow.controllers,
          outerStrength: 7,
          duration: 120,
          ease: 'Quad.easeOut',
          yoyo: true,
          hold: 0,
          onComplete: removeGlows,
        });
      } else {
        removeGlows();
      }
    }

    // Anillo de luz one-shot en la posición de la entidad (visible por todos): destello
    // dorado que se expande y se desvanece, sin partículas/esquirlas.
    if (!silent) {
      this.spawnBreakLightFlash(entityId);
    }

    if (entityId !== this.scene.localSessionId) return;

    // Fin de la ventana de protección del local → volver a la fase 'ingame' para el perfProbe.
    setPerfPhase('ingame');

    this.scene.localSpawnProtected = false;
    if (this.scene.spawnProtectionOverlay) {
      const gfx = (this.scene.spawnProtectionOverlay as any)._gfx as
        | Phaser.GameObjects.Graphics
        | undefined;
      if (gfx) {
        this.scene.tweens.killTweensOf(gfx);
        gfx.setVisible(false);
      }
      this.scene.spawnProtectionOverlay.setVisible(false);
    }
    this.scene.spawnProtectionTimer?.remove();
    this.scene.spawnProtectionTimer = null;
    if (this.scene.spawnProtectionLabel) {
      const label = this.scene.spawnProtectionLabel;
      if (!silent) {
        label.setText('¡Protección terminada!');
        this.scene.tweens.killTweensOf(label);
        label.setVisible(true).setAlpha(1);
        this.scene.tweens.add({
          targets: label,
          alpha: 0,
          delay: 700,
          duration: 600,
          onComplete: () => label.setVisible(false),
        });
      } else {
        label.setVisible(false);
      }
    }
  }

  /**
   * Aplica un halo dorado (preFX.addGlow) sobre todos los sprites de una entidad y
   * devuelve los sprites + controllers para poder pulsarlos y removerlos luego.
   * Espeja `applyImpairTint` en cómo enumera los sprites. `preFX` es no-op en Canvas
   * (el juego corre WebGL: el grayscale de muerte usa postFX), por eso el guard.
   */
  public applySpawnProtectionGlow(
    entityId: string
  ): { sprites: Phaser.GameObjects.Sprite[]; controllers: Phaser.FX.Glow[] } | null {
    const render = this.scene.playerObjects.get(entityId) ?? this.scene.npcObjects.get(entityId);
    if (!render) return null;
    const r = render as unknown as Record<string, Phaser.GameObjects.Sprite | null | undefined>;
    const candidates = [r.sprite, r.head, r.helm, r.boots, r.weapon, r.shield];
    const sprites: Phaser.GameObjects.Sprite[] = [];
    const controllers: Phaser.FX.Glow[] = [];
    for (const s of candidates) {
      if (!s || !s.preFX) continue;
      // Padding para que el halo no se recorte contra el borde del frame del sprite.
      s.preFX.setPadding(6);
      // outer sutil, sin glow interior: contorno luminoso pegado al cuerpo.
      const ctrl = s.preFX.addGlow(GameScene.SPAWN_PROTECTION_GLOW, 1.2, 0);
      sprites.push(s);
      controllers.push(ctrl);
    }
    return { sprites, controllers };
  }

  /**
   * Quita el halo de una entrada (si los sprites siguen vivos). `preFX` no expone un
   * `remove(controller)`; usamos `clear()` por sprite — el glow es el único preFX que
   * este código agrega a esos sprites, así que no pisa otros efectos.
   */
  public removeSpawnProtectionGlow(glow: { sprites: Phaser.GameObjects.Sprite[] }): void {
    for (const s of glow.sprites) {
      if (s && s.preFX) s.preFX.clear();
    }
  }

  /**
   * Destello de luz one-shot en la posición de la entidad para el quiebre de la
   * protección: un anillo dorado que se expande y se desvanece (blend ADD), sin
   * partículas. Técnica de los glows de items en el suelo (createGround*Effect).
   */
  public spawnBreakLightFlash(entityId: string): void {
    const render = this.scene.playerObjects.get(entityId) ?? this.scene.npcObjects.get(entityId);
    if (!render) return;
    const body = (render as unknown as { sprite?: Phaser.GameObjects.Sprite }).sprite;
    if (!body) return;
    const x = body.x;
    const y = body.y;
    const depth = body.depth + 1;
    // Núcleo brillante + anillo, ambos ADD, expanden y se apagan.
    const core = this.scene.add
      .circle(x, y, 16, 0xffe9a8, 0.9)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(depth)
      .setScrollFactor(1);
    const ring = this.scene.add
      .circle(x, y, 18, 0xffd86b, 0)
      .setStrokeStyle(3, 0xffe9a8, 0.85)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(depth)
      .setScrollFactor(1);
    this.scene.tweens.add({
      targets: core,
      scale: { from: 0.4, to: 1.5 },
      alpha: { from: 0.9, to: 0 },
      duration: 380,
      ease: 'Quad.easeOut',
      onComplete: () => core.destroy(),
    });
    this.scene.tweens.add({
      targets: ring,
      scale: { from: 0.4, to: 1.8 },
      alpha: { from: 0.85, to: 0 },
      duration: 440,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    });
  }

}
